import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Send, ChevronLeft, Check, X, BadgeCheck, Repeat as RepeatIcon } from "lucide-react";
import { api, imgSrc, BACKEND_URL } from "@/lib/api";
import { trackOfferAction } from "@/lib/analytics";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import CounterOfferModal from "@/components/CounterOfferModal";

function relativeTime(iso) {
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "now";
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const days = Math.floor(h / 24);
  if (days < 7) return `${days}d`;
  return d.toLocaleDateString();
}

function ProposalCard({ offer, currentUserId, onAction, onCounter }) {
  const incoming = offer.to_user_id === currentUserId;
  const status = offer.status;
  return (
    <div className="vyrti-card !shadow-none border border-[#0654BA]/20 bg-[#F8FAFF] p-4 my-2" data-testid="message-proposal">
      <div className="flex items-center gap-2 text-xs font-semibold text-[#0654BA] mb-2">
        {offer.is_counter_of ? "🔄 Counter-offer" : "📦 Trade proposal"} · <span className="uppercase">{status}</span>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <p className="text-[11px] text-zinc-500 mb-1">{incoming ? "They offered" : "You offered"}</p>
          <div className="space-y-1.5">
            {(offer.offered_listings || []).map(l => (
              <div key={l.id} className="flex items-center gap-2 bg-white rounded-xl p-2">
                {l.images?.[0] && <img src={imgSrc(l.images[0])} alt="" className="w-10 h-10 rounded-lg object-cover" />}
                <p className="text-xs font-medium truncate">{l.title}</p>
              </div>
            ))}
          </div>
        </div>
        <div>
          <p className="text-[11px] text-zinc-500 mb-1">{incoming ? "For your" : "For their"}</p>
          <div className="space-y-1.5">
            {(offer.target_listings || (offer.target_listing ? [offer.target_listing] : [])).map(l => (
              <div key={l.id} className="flex items-center gap-2 bg-white rounded-xl p-2">
                {l.images?.[0] && <img src={imgSrc(l.images[0])} alt="" className="w-10 h-10 rounded-lg object-cover" />}
                <p className="text-xs font-medium truncate">{l.title}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
      {incoming && status === "pending" && (
        <div className="flex flex-wrap gap-2 mt-3">
          <button onClick={() => onAction(offer.id, "accept")} className="btn-pill btn-accent flex-1 min-w-[100px] !py-2 !text-sm" data-testid={`accept-${offer.id}`}>
            <Check className="w-4 h-4" /> Accept
          </button>
          <button onClick={() => onCounter(offer)} className="btn-pill !py-2 !text-sm flex-1 min-w-[100px] !bg-[#E0EDFA] !text-[#0654BA] hover:!bg-[#dbeafe]" data-testid={`counter-${offer.id}`}>
            <RepeatIcon className="w-4 h-4" /> Counter
          </button>
          <button onClick={() => onAction(offer.id, "reject")} className="btn-pill btn-soft flex-1 min-w-[100px] !py-2 !text-sm" data-testid={`reject-${offer.id}`}>
            <X className="w-4 h-4" /> Decline
          </button>
        </div>
      )}
      {!incoming && status === "pending" && (
        <button onClick={() => onAction(offer.id, "cancel")} className="btn-pill btn-soft mt-3 !py-2 !text-sm" data-testid={`cancel-${offer.id}`}>
          Cancel offer
        </button>
      )}
    </div>
  );
}

export default function Messages() {
  const { id: convId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [active, setActive] = useState(null);
  const [text, setText] = useState("");
  const [loadingList, setLoadingList] = useState(true);
  const [loadingActive, setLoadingActive] = useState(false);
  const [counterOffer, setCounterOffer] = useState(null);
  const scrollRef = useRef(null);
  const wsRef = useRef(null);

  const loadList = useCallback(async () => {
    setLoadingList(true);
    try {
      const { data } = await api.get("/conversations");
      setConversations(data.items || []);
      if (!convId && data.items?.[0]) {
        navigate(`/messages/${data.items[0].id}`, { replace: true });
      }
    } catch (e) {
      console.warn("Conversation list load failed", e);
    } finally {
      setLoadingList(false);
    }
  }, [convId, navigate]);

  const loadActive = useCallback(async () => {
    if (!convId) { setActive(null); return; }
    setLoadingActive(true);
    try {
      const { data } = await api.get(`/conversations/${convId}`);
      setActive(data);
      setTimeout(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, 50);
    } catch (e) {
      toast.error("Could not load conversation");
    } finally {
      setLoadingActive(false);
    }
  }, [convId]);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { loadActive(); }, [loadActive]);

  // Real-time messaging via WebSocket — replaces polling
  useEffect(() => {
    if (!convId) return;
    // Auth via httpOnly session cookie — sent automatically on the WS handshake (same-origin)
    const wsUrl = BACKEND_URL.replace(/^http/, "ws") + `/api/ws/conversations/${convId}`;
    let ws;
    let pingInterval;
    try {
      ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === "message" && data.message) {
            // Refetch to get full hydration (trade_offer for proposal cards, etc.)
            loadActive();
            loadList();
          }
        } catch (e) { /* ignore */ }
      };
      ws.onopen = () => {
        pingInterval = setInterval(() => {
          try { ws.readyState === 1 && ws.send("ping"); } catch (e) { /* ignore */ }
        }, 25000);
      };
      ws.onerror = () => { /* silent — fallback to polling below */ };
      ws.onclose = () => { if (pingInterval) clearInterval(pingInterval); };
    } catch (e) { /* ignore */ }
    return () => {
      if (pingInterval) clearInterval(pingInterval);
      try { ws && ws.close(); } catch (e) { /* ignore */ }
      wsRef.current = null;
    };
  }, [convId, loadActive, loadList]);

  // Lightweight polling fallback every 20s in case WS drops
  useEffect(() => {
    if (!convId) return;
    const t = setInterval(loadActive, 20000);
    return () => clearInterval(t);
  }, [convId, loadActive]);

  const send = async () => {
    if (!text.trim() || !convId) return;
    try {
      await api.post("/messages", { conversation_id: convId, content: text });
      setText("");
      loadActive();
    } catch (e) {
      toast.error("Could not send");
    }
  };

  const handleOfferAction = async (offerId, action) => {
    try {
      const { data } = await api.post(`/trade-offers/${offerId}/action`, { action });
      trackOfferAction(action, data);
      toast.success(`Offer ${action}ed`);
      loadActive();
    } catch (e) {
      toast.error("Could not update");
    }
  };

  return (
    <div className="section-pad py-4 md:py-8" data-testid="messages-page">
      <h1 className="font-display font-bold text-2xl md:text-3xl tracking-tight mb-6 hidden md:block">Messages</h1>
      <div className="grid grid-cols-1 md:grid-cols-[320px_1fr] gap-4 md:gap-6 min-h-[60vh]">
        {/* List */}
        <aside className={`vyrti-card overflow-hidden ${convId ? "hidden md:block" : ""}`} data-testid="conversation-list">
          <div className="p-4 border-b border-[#E2E2E2]">
            <p className="font-display font-semibold">Inbox</p>
          </div>
          <div className="max-h-[70vh] overflow-y-auto">
            {loadingList ? (
              <div className="p-4 space-y-3">
                {Array.from({ length: 4 }).map((_, i) => <div key={`skel-${i}`} className="skeleton h-14" />)}
              </div>
            ) : conversations.length === 0 ? (
              <div className="p-6 text-center text-sm text-zinc-500">No conversations yet. Browse trades and propose one!</div>
            ) : conversations.map(c => (
              <Link key={c.id} to={`/messages/${c.id}`} className={`flex items-center gap-3 px-4 py-3 border-b border-[#E2E2E2] hover:bg-[#F7F7F7] ${c.id === convId ? "bg-[#F8FAFF]" : ""}`} data-testid={`conv-${c.id}`}>
                {c.other_user?.picture ? (
                  <img src={imgSrc(c.other_user.picture)} alt="" className="w-10 h-10 rounded-full object-cover" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-[#F1F5F9] flex items-center justify-center font-semibold text-sm">{c.other_user?.name?.[0]?.toUpperCase()}</div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate flex items-center gap-1">
                    {c.other_user?.name}
                    {c.other_user?.verified && <BadgeCheck className="w-3.5 h-3.5 text-[#0654BA]" />}
                  </p>
                  <p className="text-xs text-zinc-500 truncate">{c.last_message_preview || "Start the conversation"}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-[11px] text-zinc-400">{relativeTime(c.last_message_at)}</p>
                  {c.unread_count > 0 && <span className="inline-block mt-1 text-[10px] bg-[#0654BA] text-white rounded-full px-1.5 py-0.5">{c.unread_count}</span>}
                </div>
              </Link>
            ))}
          </div>
        </aside>

        {/* Active chat */}
        <section className={`vyrti-card overflow-hidden flex flex-col ${!convId ? "hidden md:flex" : "flex"}`} data-testid="conversation-active">
          {!convId ? (
            <div className="flex-1 flex items-center justify-center text-sm text-zinc-500">Select a conversation</div>
          ) : !active ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="w-8 h-8 rounded-full border-4 border-zinc-200 border-t-[#0654BA] animate-spin" />
            </div>
          ) : (
            <>
              <div className="p-4 border-b border-[#E2E2E2] flex items-center gap-3">
                <button onClick={() => navigate("/messages")} className="md:hidden p-1.5 -ml-1 rounded-full hover:bg-[#F1F5F9]" data-testid="back-to-list">
                  <ChevronLeft className="w-5 h-5" />
                </button>
                {active.other_user?.picture ? (
                  <img src={imgSrc(active.other_user.picture)} alt="" className="w-10 h-10 rounded-full object-cover" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-[#F1F5F9] flex items-center justify-center font-semibold">{active.other_user?.name?.[0]?.toUpperCase()}</div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-medium flex items-center gap-1">
                    {active.other_user?.name}
                    {active.other_user?.verified && <BadgeCheck className="w-4 h-4 text-[#0654BA]" />}
                  </p>
                  {active.listing && (
                    <Link to={`/listing/${active.listing.id}`} className="text-xs text-zinc-500 hover:text-[#0654BA] truncate">About: {active.listing.title}</Link>
                  )}
                </div>
              </div>

              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-2 bg-[#F7F7F7]">
                {active.messages.map(m => {
                  if (m.type === "proposal" && m.trade_offer) {
                    return <ProposalCard key={m.id} offer={m.trade_offer} currentUserId={user.user_id} onAction={handleOfferAction} onCounter={(o) => setCounterOffer(o)} />;
                  }
                  if (m.type === "system") {
                    return <div key={m.id} className="text-center text-xs text-zinc-500 py-2" data-testid={`msg-system-${m.id}`}>{m.content}</div>;
                  }
                  const mine = m.sender_id === user.user_id;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
                      <div className={`max-w-[80%] rounded-2xl px-4 py-2 text-sm ${mine ? "bg-[#0654BA] text-white rounded-br-md" : "bg-white border border-[#E2E2E2] rounded-bl-md"}`}>
                        {m.content}
                        <span className={`block text-[10px] mt-1 ${mine ? "text-white/70" : "text-zinc-400"}`}>{relativeTime(m.created_at)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>

              <form onSubmit={e => { e.preventDefault(); send(); }} className="p-3 border-t border-[#E2E2E2] flex items-center gap-2">
                <input className="input-pill" placeholder="Type a message…" value={text} onChange={e => setText(e.target.value)} data-testid="msg-input" />
                <button type="submit" disabled={!text.trim()} className="btn-pill btn-accent disabled:opacity-50 !px-4" data-testid="msg-send">
                  <Send className="w-4 h-4" />
                </button>
              </form>
            </>
          )}
        </section>
      </div>
      <CounterOfferModal
        open={!!counterOffer}
        onClose={() => setCounterOffer(null)}
        offer={counterOffer}
        onCountered={() => { loadActive(); loadList(); }}
      />
    </div>
  );
}
